import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import auth from '../../services/authService';
import { getAssignedServices } from '../../helpers/assignments';

import AssignedPartsModal from '../modals/AssignedPartsModal';
import { ReactComponent as StarIcon } from '../../icons/star-solid.svg';
import { ReactComponent as ChevronIcon } from '../../icons/nav-arrow-right.svg';

import './AssignmentBanner.scss';

// Whether the banner has pulsed yet this visit. Module state, so coming back to the page doesn't
// replay it but a reload does.
let hasPulsed = false;

// Above the services: the soonest one the user has a part in, and how many more. A tap lists them all
const AssignmentBanner = ({ services, songs }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [pulse] = useState(() => !hasPulsed);
  const { _id: userId, firstName } = auth.getCurrentUser() ?? {};

  const assigned = useMemo(
    () => (userId ? getAssignedServices(services, songs, userId) : []),
    [services, songs, userId],
  );

  // Spent only once the banner has actually shown, not while the services are still loading
  const isShown = assigned.length > 0;

  useEffect(() => {
    if (isShown) hasPulsed = true;
  }, [isShown]);

  if (!isShown) return null;

  const more = assigned.length - 1;

  return (
    <>
      <button
        type="button"
        className={cx('AssignmentBanner', { 'has-more': more > 0, pulse })}
        onClick={() => setShowModal(true)}
      >
        <span className="star">
          <StarIcon aria-hidden="true" />
        </span>
        <span className="text">
          <span className="lead">{firstName ? t('services.hey', { name: firstName }) : t('services.heyNoName')}</span>
          <span className="title">
            <Trans
              i18nKey="services.youHaveParts"
              values={{ service: assigned[0].service.title }}
              components={{ strong: <strong /> }}
            />
          </span>
        </span>
        {more > 0 ? <span className="more">+{more}</span> : <ChevronIcon className="chevron" aria-hidden="true" />}
      </button>
      <AssignedPartsModal isOpen={showModal} onClose={() => setShowModal(false)} assigned={assigned} />
    </>
  );
};

AssignmentBanner.propTypes = {
  services: PropTypes.array.isRequired,
  songs: PropTypes.array.isRequired,
};

export default AssignmentBanner;
